import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Terminal, X, ShieldAlert, ArrowRight, Mic, Square, RotateCcw, Settings2 } from "lucide-react";
import { ORACLE_API, oracleAuthHeaders, cachedGet, clearOracleCache, CLEARANCE_TONE } from "./oracleClient";
import {
  createRecorder, transcribeAudio, synthesizeSpeech, defaultVoicePrefs,
  loadVoicePrefs, saveVoicePrefs, VOICE_OPTIONS, VOICE_STATES,
  firstGreeting,
} from "./oracleVoice";
import { OracleAnswer } from "./OracleAnswer";
import { desk } from "../member-desk/tokens";
import { useCallsign } from "../../command/useCallsign";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 5) return "Good evening";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const PRIORITY_GROUPS = [
  { key: "priority", label: "Priority Assessment", sub: "Requires action." },
  { key: "monitoring", label: "Monitoring", sub: "Worth knowing." },
  { key: "informational", label: "Informational", sub: "Routine intelligence." },
];

const Briefing = ({ briefing, tone, callsign }) => {
  const bullets = briefing.bullets || [];
  return (
    <div className="border-l-2 pl-4 py-1 mb-8 animate-in fade-in duration-300"
         style={{ borderColor: tone }} data-testid="oracle-daily-briefing">
      <div className="font-mono text-[9px] uppercase tracking-[0.35em] mb-2" style={{ color: desk.inkMuted }}>
        Oracle · {briefing.header?.clearance}
      </div>
      <p className="font-display text-[17px]" style={{ color: desk.ink }}>
        {greeting()}, Investigator.
      </p>
      <p className="text-[13px] leading-relaxed mt-1" style={{ color: desk.inkDim }}>
        Daily briefing complete.
        {bullets.length === 0 && " Nothing requires your immediate attention."}
      </p>
      {PRIORITY_GROUPS.map(({ key, label, sub }) => {
        const group = bullets.filter((b) => (b.priority || "informational") === key);
        if (!group.length) return null;
        return (
          <div key={key} className="mt-4" data-testid={`oracle-briefing-${key}`}>
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: desk.inkDim }}>
                {label}
              </span>
              <span className="font-mono text-[8px] tracking-[0.1em]" style={{ color: desk.inkMuted }}>
                {sub}
              </span>
            </div>
            <ul className="mt-1.5 space-y-1.5">
              {group.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px]" style={{ color: desk.inkDim }}
                    data-testid="oracle-briefing-bullet">
                  <span className="mt-[6px] w-1 h-1 shrink-0" style={{ background: tone }} />
                  {b.href ? (
                    <a href={b.href} className="underline underline-offset-2 hover:text-[#E4DFD3]"
                       style={{ textDecorationColor: desk.edgeHi }}>
                      {b.text}
                    </a>
                  ) : b.text}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      <p className="font-mono text-[9px] uppercase tracking-[0.3em] mt-4" style={{ color: desk.inkMuted }}>
        Standing by.
      </p>
    </div>
  );
};

/**
 * OracleWorkspace — the immersive intelligence workspace that expands
 * INSIDE the Members headquarters (a full-screen overlay panel). Never a
 * separate application. Reuses the unchanged clearance-aware backend.
 */
export default function OracleWorkspace({ open, initialQuery = "", autoVoice = false, onClose }) {
  const identity = useCallsign();
  const [me, setMe] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("STANDING BY");
  const [log, setLog] = useState([]);
  const feedRef = useRef(null);
  const inputRef = useRef(null);
  const firedRef = useRef(false);
  const logRef = useRef([]);
  const timersRef = useRef([]);
  useEffect(() => { logRef.current = log; }, [log]);

  // ---- Voice — The Archivist secure channel (silent by default) ----
  const [voicePrefs, setVoicePrefs] = useState(defaultVoicePrefs);
  const [recording, setRecording] = useState(false);
  const [voiceState, setVoiceState] = useState("");   // immersive session line
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const recorderRef = useRef(null);
  const audioRef = useRef(null);
  const lastSpokenRef = useRef(-1);
  const briefingSpokenRef = useRef(false);
  const prefsRef = useRef(voicePrefs);
  useEffect(() => { prefsRef.current = voicePrefs; }, [voicePrefs]);

  const updatePrefs = (patch) => {
    setVoicePrefs((p) => {
      const next = { ...p, ...patch };
      saveVoicePrefs(patch, p);   // syncs to account + local cache
      return next;
    });
  };

  const stopAudio = () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch { /* noop */ } audioRef.current = null; }
  };

  const synth = useCallback(async (text, { force = false } = {}) => {
    const p = prefsRef.current;
    if (!text) return;
    if (!force && (!p.enabled || p.preset === "text")) return;
    try {
      setVoiceState(VOICE_STATES.speaking);
      const { audio_base64, mime } = await synthesizeSpeech(
        text, p.preset === "text" ? "classic" : p.preset, p.speed);
      stopAudio();
      const el = new Audio(`data:${mime || "audio/mp3"};base64,${audio_base64}`);
      audioRef.current = el;
      el.onended = () => setVoiceState(VOICE_STATES.done);
      await el.play().catch(() => setVoiceState(""));
    } catch { setVoiceState(""); }
  }, []);

  const replayLast = () => {
    const last = logRef.current[logRef.current.length - 1];
    if (last?.res?.narrative) synth(last.res.narrative, { force: true });
  };

  // Enable a voice preset — play the one-time welcome on first enable.
  const chooseVoice = (presetId) => {
    if (presetId === "text") {
      stopAudio();
      updatePrefs({ enabled: false, preset: "text" });
      return;
    }
    const first = !prefsRef.current.greeted;
    updatePrefs({ enabled: true, preset: presetId, ...(first ? { greeted: true } : {}) });
    prefsRef.current = { ...prefsRef.current, enabled: true, preset: presetId };
    if (first) synth(firstGreeting(identity?.callsign), { force: true });
  };

  const toggleMic = useCallback(async () => {
    if (recording) {
      setRecording(false);
      setVoiceState(VOICE_STATES.transcribing);
      try {
        const blob = await recorderRef.current?.stop();
        recorderRef.current = null;
        const text = await transcribeAudio(blob);
        setVoiceState("");
        if (text) { setQ(text); ask(text); }
      } catch { setVoiceState(""); }
      return;
    }
    stopAudio();
    try {
      recorderRef.current = await createRecorder();
      recorderRef.current.start();
      setRecording(true);
      setVoiceState(VOICE_STATES.listening);
    } catch {
      setVoiceState("MICROPHONE UNAVAILABLE");
      setTimeout(() => setVoiceState(""), 2500);
    }
  }, [recording]);

  const setStatusLater = (label, ms) => {
    timersRef.current.push(setTimeout(() => setStatus(label), ms));
  };
  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const ask = useCallback(async (query) => {
    const text = (query ?? "").trim();
    if (!text || busy) return;
    setBusy(true);
    clearTimers();
    setStatus("ANALYZING");
    setStatusLater("CROSS-REFERENCING", 1400);
    // Session-only continuity — each prior turn is tagged with the role it
    // was answered under so the server can re-authorize every follow-up.
    const history = logRef.current.slice(-4).map((e) => ({
      q: e.q,
      a: e.res?.narrative || "",
      role: e.res?.header?.role || "",
    }));
    try {
      const r = await axios.post(`${ORACLE_API}/oracle/console/ask`, { query: text, history },
        { headers: { ...oracleAuthHeaders(), "Content-Type": "application/json" } });
      setLog((l) => [...l, { q: text, res: r.data }]);
    } catch {
      setLog((l) => [...l, { q: text, res: {
        authorized: true,
        header: { clearance: "PUBLIC CLEARANCE", subtitle: "Oracle is temporarily unreachable." },
        narrative: "Oracle could not complete that request. Try again shortly.", data: null } }]);
    } finally {
      setBusy(false);
      setQ("");
      clearTimers();
      setStatus("ANALYSIS COMPLETE");
      setStatusLater("MONITORING", 2500);
    }
  }, [busy]);

  // Load clearance + the Daily Briefing when opened (short-lived cache
  // keeps re-opens instant; keyed to credentials so roles never mix).
  useEffect(() => {
    if (!open) { setBriefing(null); setStatus("STANDING BY"); clearTimers(); return; }
    (async () => {
      try {
        const [meData, briefData] = await Promise.all([
          cachedGet(axios, "/oracle/console/me"),
          cachedGet(axios, "/oracle/console/briefing"),
        ]);
        // Role changed since cache was written → drop everything and refetch
        // so a stale higher-clearance briefing can never be shown.
        if (meData?.clearance?.role && briefData?.header?.role &&
            meData.clearance.role !== briefData.header.role) {
          clearOracleCache();
          setMe(await cachedGet(axios, "/oracle/console/me"));
          setBriefing(await cachedGet(axios, "/oracle/console/briefing"));
        } else {
          setMe(meData);
          setBriefing(briefData);
        }
      } catch {
        setMe(null);
        setBriefing(null);
      }
    })();
    return clearTimers;
  }, [open]);

  // Fire the query the member typed on the dashboard bar, once per open.
  useEffect(() => {
    if (open && initialQuery && !firedRef.current) {
      firedRef.current = true;
      ask(initialQuery);
    }
    if (open && !initialQuery && autoVoice && !firedRef.current) {
      firedRef.current = true;
      setTimeout(() => toggleMic(), 350);
    }
    if (!open) { firedRef.current = false; setLog([]); setQ(""); }
  }, [open, initialQuery, autoVoice, ask]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && inputRef.current) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [log, busy]);

  // Auto-speak each newly delivered briefing (once) — ONLY when the member
  // has intentionally enabled voice. Oracle never speaks unprompted.
  useEffect(() => {
    if (!open || busy || log.length === 0) return;
    const idx = log.length - 1;
    if (idx <= lastSpokenRef.current) return;
    lastSpokenRef.current = idx;
    const narrative = log[idx]?.res?.narrative;
    if (narrative && prefsRef.current.enabled && prefsRef.current.preset !== "text") {
      synth(narrative);
    }
  }, [log, busy, open, synth]);

  // Load account-synced voice prefs + establish/tear down session line.
  useEffect(() => {
    if (open) {
      loadVoicePrefs().then(setVoicePrefs);
      setVoiceState(VOICE_STATES.open);
    } else {
      stopAudio(); setRecording(false); lastSpokenRef.current = -1;
      briefingSpokenRef.current = false;
      setVoiceState(""); recorderRef.current = null;
    }
  }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Optional: narrate the Daily Briefing on open — only if the member has
  // enabled voice AND opted into auto-play briefing (both OFF by default).
  useEffect(() => {
    if (!open || !briefing || briefingSpokenRef.current) return;
    const p = prefsRef.current;
    if (!p.enabled || p.preset === "text" || !p.autoplay_briefing) return;
    briefingSpokenRef.current = true;
    const priority = (briefing.bullets || [])
      .filter((b) => (b.priority || "") === "priority").slice(0, 3)
      .map((b) => b.text);
    const spoken = ["Daily briefing ready.", ...priority].join(" ");
    synth(spoken);
  }, [open, briefing, synth]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const tone = CLEARANCE_TONE[me?.clearance?.label] || "#A68F5E";

  return createPortal((
    <div className="fixed inset-0 z-[9999] flex flex-col animate-in fade-in duration-200"
         style={{ background: "rgba(6,5,8,0.98)", backdropFilter: "blur(6px)" }}
         data-testid="oracle-workspace">
      <div className="w-full max-w-3xl mx-auto flex-1 flex flex-col px-4 md:px-6 py-5 md:py-10 min-h-0"
           style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
        {/* header — still clearly HQ, not a new app */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <Terminal size={16} className="shrink-0" style={{ color: tone }} />
            <span className="font-display text-xl" style={{ color: desk.ink }}>Oracle</span>
            {me && (
              <span className="hidden sm:inline-flex ml-2 items-center gap-1.5 border px-2 py-1"
                    style={{ borderColor: tone, background: "#100F13" }}
                    data-testid="oracle-current-clearance">
                <ShieldAlert size={10} style={{ color: tone }} />
                <span className="font-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: tone }}>
                  {me.clearance.label}
                </span>
              </span>
            )}
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] transition-opacity duration-300 truncate"
                  style={{ color: desk.inkMuted }} data-testid="oracle-status">
              ● {voiceState || status}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setShowVoiceSettings((v) => !v)} data-testid="oracle-voice-settings-toggle"
                    title="Voice settings"
                    className="inline-flex items-center justify-center border w-9 h-9 min-h-[36px] transition-colors hover:text-[#E4DFD3]"
                    style={{ borderColor: showVoiceSettings ? tone : desk.edge, color: desk.inkDim }}>
              <Settings2 size={13} />
            </button>
            <button onClick={onClose} data-testid="oracle-close"
                    className="inline-flex shrink-0 items-center gap-1.5 border px-3 py-2 min-h-[44px] sm:min-h-0 transition-colors hover:text-[#E4DFD3]"
                    style={{ borderColor: desk.edge, color: desk.inkDim }}>
              <span className="font-mono text-[9px] uppercase tracking-[0.25em]">Back to HQ</span>
              <X size={12} />
            </button>
          </div>
        </div>

        {showVoiceSettings && (
          <div className="mt-4 border p-4 animate-in fade-in duration-200"
               style={{ borderColor: desk.edgeHi, background: "#100F13" }}
               data-testid="oracle-voice-settings">
            <div className="font-mono text-[9px] uppercase tracking-[0.35em] mb-3" style={{ color: tone }}>
              Secure Channel · Voice
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {VOICE_OPTIONS.map((opt) => {
                const active = opt.id === "text"
                  ? (!voicePrefs.enabled || voicePrefs.preset === "text")
                  : (voicePrefs.enabled && voicePrefs.preset === opt.id);
                return (
                  <button key={opt.id} onClick={() => chooseVoice(opt.id)}
                          data-testid={`oracle-voice-preset-${opt.id}`}
                          className="text-left border px-3 py-2 transition-colors"
                          style={{ borderColor: active ? tone : desk.edge,
                                   background: active ? "#161318" : "transparent" }}>
                    <div className="font-mono text-[11px]" style={{ color: desk.ink }}>{opt.label}</div>
                    <div className="font-mono text-[9px]" style={{ color: desk.inkMuted }}>{opt.desc}</div>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-4">
              <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]"
                     style={{ color: voicePrefs.enabled ? desk.inkDim : desk.inkMuted }}>
                <input type="checkbox" checked={!!voicePrefs.autoplay_briefing}
                       disabled={!voicePrefs.enabled}
                       onChange={(e) => updatePrefs({ autoplay_briefing: e.target.checked })}
                       data-testid="oracle-voice-autoplay" className="accent-[#A68F5E]" />
                Auto-play briefing
              </label>
              <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: desk.inkDim }}>
                Speed
                <input type="range" min="0.75" max="1.5" step="0.05" value={voicePrefs.speed}
                       onChange={(e) => updatePrefs({ speed: parseFloat(e.target.value) })}
                       data-testid="oracle-voice-speed" className="w-28 accent-[#A68F5E]" />
                <span style={{ color: desk.ink }}>{Number(voicePrefs.speed).toFixed(2)}×</span>
              </label>
              <button onClick={replayLast} data-testid="oracle-voice-replay"
                      className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]"
                      style={{ color: desk.inkDim }}>
                <RotateCcw size={13} /> Replay last
              </button>
            </div>
            <p className="font-mono text-[8px] mt-3" style={{ color: desk.inkMuted }}>
              Voice is off by default. Every spoken briefing also appears as text —
              voice is never required. Your preference syncs across devices.
            </p>
          </div>
        )}

        {me?.example_commands?.length > 0 && log.length === 0 && !busy && (
          <div className="mt-6" data-testid="oracle-examples">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] mb-2" style={{ color: desk.inkMuted }}>
              What would you like to know, {identity?.callsign || "Investigator"}?
            </div>
            <div className="flex flex-wrap gap-2">
              {me.example_commands.map((c) => (
                <button key={c} onClick={() => ask(c)} data-testid="oracle-example-chip"
                        className="border px-3 py-2 font-mono text-[10px] text-[#B8B2A6] hover:border-[#A68F5E] hover:text-[#E4DFD3] transition-colors"
                        style={{ borderColor: desk.edge, background: "#100F13" }}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={feedRef} className="flex-1 overflow-y-auto mt-6 pr-1 min-h-0 overscroll-contain"
             style={{ WebkitOverflowScrolling: "touch" }}>
          {briefing && <Briefing briefing={briefing} tone={tone} callsign={identity?.callsign} />}
          {log.map((entry, i) => (
            <div key={i} className="animate-in fade-in duration-300">
              <div className="flex items-center gap-2 mb-2">
                <ArrowRight size={11} style={{ color: desk.inkMuted }} />
                <span className="font-mono text-[11px]" style={{ color: desk.inkDim }}>{entry.q}</span>
              </div>
              <OracleAnswer res={entry.res} />
            </div>
          ))}
          {busy && (
            <div className="font-mono text-[9px] uppercase tracking-[0.4em] animate-pulse py-2"
                 style={{ color: desk.inkMuted }} data-testid="oracle-thinking">
              ● {status}
            </div>
          )}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); ask(q); }} className="mt-4 flex gap-2" data-testid="oracle-form">
          <input
            ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Ask Oracle…" data-testid="oracle-input"
            className="flex-1 min-w-0 bg-[#100F13] border px-4 py-3 text-[16px] md:text-[14px] font-mono placeholder:text-[#4A463F] focus:outline-none focus:border-[#A68F5E] transition-colors"
            style={{ borderColor: desk.edge, color: desk.ink }}
          />
          <button type="button" onClick={toggleMic} disabled={busy}
                  data-testid="oracle-mic"
                  title={recording ? "Stop transmission" : "Open secure voice channel"}
                  className="min-h-[44px] w-12 inline-flex items-center justify-center border transition-colors disabled:opacity-40"
                  style={{ borderColor: recording ? desk.redact : desk.edge,
                           background: recording ? desk.redact : "#100F13",
                           color: recording ? "#fff" : desk.inkDim }}>
            {recording ? <Square size={14} className="animate-pulse" /> : <Mic size={16} />}
          </button>
          <button type="submit" disabled={busy || !q.trim()} data-testid="oracle-submit"
                  className="min-h-[44px] px-5 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#B92318] disabled:opacity-40"
                  style={{ backgroundColor: desk.redact }}>
            Ask
          </button>
        </form>
      </div>
    </div>
  ), document.body);
}
